import { Box, Link as MuiLink, Typography, useTheme } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { xsHeight, mdHeight } from "components/AppBar";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { tabsOffset } from "components/TabLinks";
import { getFullName } from "encamp-shared/src/utils/name";
import { gql } from "generated-graphql";
import { ContactReportRole, FacilityContactsSearchQuery } from "generated-graphql/graphql";
import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { contactRoleToLabel } from "util/facilityContacts";
import { formatPhoneNumberAndType } from "util/phoneNumbers";

export const FACILITY_CONTACTS_SECTION_ID = "facility-contacts";
export const FACILITY_CONTACTS_SECTION_ICON = "📇";

const FACILITY_CONTACTS_SEARCH = gql(`
  query FacilityContactsSearch(
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
  ) {
    facilityContactsSearch(
      search: $search
      page: $page
      pageSize: $pageSize
      sort: $sort
    ) {
      items {
        id
        reportingRoles
        isPrimaryContact
        person {
          id
          first
          last
          email
          phones {
            type
            number
          }
        }
      }
      count
    }
  }
`);

type Row = FacilityContactsSearchQuery["facilityContactsSearch"]["items"][number];

export const FacilityContactsSection: React.FC = () => {
  const theme = useTheme();
  const { tenantId, facilityId } = useParams<{ tenantId: string; facilityId: string }>();

  const columns: GridColDef<Row>[] = useMemo(
    () => [
      {
        field: "reportingRoles",
        headerName: "Role",
        flex: 1,
        filterKeyType: "enumAutocomplete",
        enumValues: Object.values(ContactReportRole),
        enumPresentationFunction: (enumValue: string) =>
          contactRoleToLabel(enumValue as ContactReportRole),
        valueGetter: (_, row) =>
          row.reportingRoles?.map((role) => contactRoleToLabel(role)).join(", ") ?? "",
        renderCell: ({ row }) => (
          <Box sx={{ display: "flex", flexDirection: "column", py: 1 }}>
            {row.reportingRoles?.map((role, i) => (
              <Typography key={i} variant="body2">
                {contactRoleToLabel(role)}
              </Typography>
            ))}
          </Box>
        ),
      },
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        valueGetter: (_, row) => getFullName(row.person),
      },
      {
        field: "phone",
        headerName: "Phone",
        flex: 1.25,
        sortable: false,
        valueGetter: (_, row) =>
          row.person.phones?.map((phone) => formatPhoneNumberAndType(phone)).join(", ") ?? "",
        renderCell: ({ row }) => (
          <Box sx={{ display: "flex", flexDirection: "column", py: 1 }}>
            {row.person.phones?.map((phone, i) => (
              <Typography key={i} variant="body2">
                {formatPhoneNumberAndType(phone)}
              </Typography>
            ))}
          </Box>
        ),
      },
      {
        field: "email",
        headerName: "Email",
        flex: 1.5,
        valueGetter: (_, row) => row.person.email ?? "",
      },
    ],
    []
  );

  return (
    <Box
      id={FACILITY_CONTACTS_SECTION_ID}
      sx={{
        scrollMarginTop: {
          xs: theme.spacing(xsHeight + tabsOffset + 11),
          md: theme.spacing(mdHeight + tabsOffset + 11),
        },
      }}
    >
      <Typography variant="h6" mb={5}>
        {FACILITY_CONTACTS_SECTION_ICON} Facility Contacts
      </Typography>

      <Typography variant="body2" color="text.secondary" mb={3}>
        Showing all current facility contacts. To see specific requirements for your program area,
        view{" "}
        <MuiLink component={Link} to={`/o/${tenantId}/chemicals/contacts/${facilityId}`}>
          Chemicals Contacts
        </MuiLink>
        .
      </Typography>

      <OmnisearchDataGrid
        persistenceKey={`facility-contacts-v2-${facilityId}`}
        columns={columns}
        dataQuery={FACILITY_CONTACTS_SEARCH}
        defaultSearch={`facilityId:${facilityId}`}
        getItems={(data) => data.facilityContactsSearch.items}
        getCount={(data) => data.facilityContactsSearch.count}
        initialSortModel={[{ field: "name", sort: "asc" }]}
        noDataMessage="No contacts for this facility."
        excludeFilterColumns={["phone", "reportingRoles"]}
        additionalFilterColumns={[
          {
            key: "reportingRole",
            header: "Role",
            filterKeyType: "enumAutocomplete",
            enumValues: Object.values(ContactReportRole),
            enumPresentationFunction: (enumValue) =>
              contactRoleToLabel(enumValue as ContactReportRole),
          },
        ]}
        rowHeight="auto"
        disableRowSelectionOnClick
        gridSx={{
          "& .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within, .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within":
            { outline: "none" },
          "& .MuiDataGrid-columnSeparator": { display: "none" },
          "& .MuiDataGrid-row:hover": {
            cursor: "default",
            backgroundColor: "transparent",
          },
          "& .MuiDataGrid-row.Mui-hovered": {
            backgroundColor: "transparent",
          },
        }}
      />
    </Box>
  );
};
